/**
 * Shared cover-letter prompt construction, used by both AISettingsPage.vue
 * (the full AI Settings page) and JobDetails.vue (the quick inline panel),
 * so the two surfaces can never drift out of sync on wording.
 */

export const STRATEGY_MAP = {
  job_description: "Focus primarily on matching the specific language and requirements from the job description. Mirror the employer's terminology.",
  balanced: "Balance between the job requirements and the candidate's background — connect both throughout the letter.",
  resume: "Lead with the candidate's experience and accomplishments, then connect them to the job requirements.",
};

export const TONE_MAP = {
  professional: "Adopt a confident, polished tone that sounds like a seasoned professional. Keep wording precise, respectful, and businesslike while still sounding personable.",
  friendly: "Write with upbeat, positive energy as if speaking to a friendly colleague. Use warm and encouraging language, sprinkle in light enthusiasm.",
  formal: "Use highly refined, traditional business language. Maintain a ceremonious tone with complete sentences, courteous phrasing, and elevated vocabulary.",
  conversational: "Sound natural and relaxed, as though chatting with the hiring manager over coffee. Keep sentences flowing and use everyday language.",
};

export const LENGTH_MAP = {
  short: "Keep the cover letter very concise and brief (approximately 1-2 paragraphs, around 100-150 words). Be extremely direct and to the point, focusing only on the most essential qualifications and key selling points. Avoid unnecessary elaboration.",
  medium: "Write a medium-length cover letter (approximately 3-4 paragraphs, around 250-400 words). Provide adequate detail while remaining focused and readable.",
  long: "Write a comprehensive cover letter (approximately 4-5 paragraphs, around 400-600 words) with detailed explanations. Elaborate on relevant experiences, skills, and achievements that align with the job requirements.",
};

export const FOCUS_MAP = {
  technical: "Emphasize and highlight technical skills, programming languages, tools, frameworks, and technical achievements mentioned in the resume. Provide specific examples of technical projects and expertise that directly relate to the job requirements.",
  leadership: "Focus extensively on leadership experience, team management, and examples of leading projects or teams. Highlight instances where you guided, mentored, or managed others, and describe the impact of your leadership.",
  experience: "Prioritize and elaborate on relevant work experience that matches the job requirements. Draw clear connections between your past roles and the responsibilities of this position. Provide specific examples from your work history.",
  relevant_experience: "Prioritize and elaborate on relevant work experience that matches the job requirements. Draw clear connections between your past roles and the responsibilities of this position.",
  achievements: "Showcase specific achievements, quantifiable results, and notable accomplishments from the resume. Use numbers, metrics, and concrete examples to demonstrate your success and impact in previous roles.",
  communication: "Highlight strong communication and collaboration skills. Show how you work effectively with teams, clients, or stakeholders and convey ideas clearly.",
  client_support: "Emphasize client support, responsiveness, and problem-solving for clients. Show how you deliver excellent service and maintain positive client relationships.",
};

export const STARTER_PRESET_MAP = {
  short_direct: "Write a very short, direct cover letter. Get to the point fast. Be concise and professional.",
  beginner_friendly: "Highlight willingness to learn, adaptability, and enthusiasm. Acknowledge being newer to the field and compensate with eagerness.",
  client_focused: "Emphasize solving business problems, client communication skills, and reliability. Show how you deliver results for clients.",
  tech_focused: "Lead with technical stack, programming experience, and development depth. Be specific about tools, frameworks, and technical achievements.",
  remote_ready: "Highlight remote work experience, async communication skills, self-management, and reliability. Mention tools used for remote collaboration (e.g. Slack, Notion, Trello, Zoom). Emphasize time zone flexibility and a track record of delivering results independently without supervision.",
  results_driven: "Open with the most impressive measurable achievement relevant to the role. Throughout the letter, use specific numbers, percentages, or outcomes wherever possible. Show impact, not just duties.",
};

/**
 * Resolve the prose for the active preset/template, or "" when none is active.
 * Shared by the v1 and v2 builder paths and by any component (e.g. AutoApplyPage)
 * that needs to display the active template's text without reassembling a prompt.
 *
 * @param {string} activePreset - starter preset key, or "custom-<index>"
 * @param {Array<{text: string}>} customPresets
 * @returns {string}
 */
export function resolveActivePresetText(activePreset, customPresets = []) {
  if (!activePreset) return "";
  if (String(activePreset).startsWith("custom-")) {
    const idx = parseInt(String(activePreset).replace("custom-", ""), 10);
    const preset = customPresets[idx];
    return preset?.text ? preset.text : "";
  }
  return STARTER_PRESET_MAP[activePreset] || "";
}

/**
 * @param {object} config
 * @param {number} [config.version] - when 2, skip tone/length/strategy/focus/preference
 *   assembly and use only the active template's text (Vollna-style instructions).
 * @param {string} [config.strategy]
 * @param {string} [config.tone]
 * @param {string} [config.length]
 * @param {string[]} [config.focusAreas]
 * @param {object} [config.preferences] - { avoid_ai_phrases, keep_natural, avoid_overly_formal, short_intro }
 * @param {string} [config.activePreset] - starter preset key, or "custom-<index>"
 * @param {Array<{text: string}>} [config.customPresets]
 * @param {string} [config.selectedInstructionText] - free-text saved instruction, used when no preset is active
 * @returns {string}
 */
export function buildCoverLetterPrompt({
  version,
  strategy,
  tone,
  length,
  focusAreas = [],
  preferences = {},
  activePreset = null,
  customPresets = [],
  selectedInstructionText = null,
} = {}) {
  if (version === 2) {
    return resolveActivePresetText(activePreset, customPresets).trim();
  }

  const parts = [];

  if (STRATEGY_MAP[strategy]) parts.push(STRATEGY_MAP[strategy]);
  if (TONE_MAP[tone]) parts.push(TONE_MAP[tone]);
  if (LENGTH_MAP[length]) parts.push(LENGTH_MAP[length]);
  focusAreas.forEach((area) => {
    if (FOCUS_MAP[area]) parts.push(FOCUS_MAP[area]);
  });

  const prefParts = [];
  if (preferences.avoid_ai_phrases) prefParts.push("Avoid generic AI phrases like 'I am excited to apply', 'I am passionate about', or 'I would be a great fit'.");
  if (preferences.keep_natural) prefParts.push("Keep the writing natural and human — avoid overly polished or robotic sentences.");
  if (preferences.avoid_overly_formal) prefParts.push("Avoid overly formal language — write like a real person, not a legal document.");
  if (preferences.short_intro) prefParts.push("Keep the introduction short and direct — lead with value immediately, no lengthy opener paragraphs.");
  if (prefParts.length) parts.push(prefParts.join(" "));

  const presetText = resolveActivePresetText(activePreset, customPresets);
  if (presetText) {
    parts.push(presetText);
  } else if (selectedInstructionText) {
    parts.push(selectedInstructionText);
  }

  return parts.join(" ");
}
